import {format} from '@telia/cpa-web-common';
import {ActiveStatus, activeStatus} from '@telia/cpa-web-common/dist/period';
import {FullWidthTable} from '@telia/styleguide';
import {useThSortable} from '@telia/styleguide/atoms/FullWidthTable/useThSortable';
import classnames from 'classnames';
import React, {FC, useMemo} from 'react';

import {useModal} from '../../hooks/useModal';
import {useProductFeatures} from '../../hooks/useProductFeatures';
import {useProductTypes} from '../../hooks/useProductTypes';
import {useShowInactive} from '../../hooks/useShowInactive';
import {Product, ProductFeature} from '../../model';
import {Icon} from '../common/Icon';
import InformationLineFc from '../common/InformationLine';
import List from '../common/List';
import ListItem from '../common/ListItem';
import Tooltip from '../common/Tooltip';

interface FeatureTableProps {
  product: Product;
  showActions: boolean;
  saveCallback: (product: Product) => void;
  onUpdate: (feature: ProductFeature) => void;
}

const getAccessNumberName = (featureTypeId: string): string => {
  switch (featureTypeId) {
    case 'RECEIVE_ON_SHORT_NUMBER':
      return 'Short Number';
    case 'RECEIVE_ON_LONG_NUMBER':
      return 'Long Number';
    default:
      return 'Access Number';
  }
};

export const FeaturesTable: FC<FeatureTableProps> = ({product, showActions, saveCallback, onUpdate}) => {
  const {showModal} = useModal();
  const {getProductTypeFeature} = useProductTypes();
  const {getFeature} = useProductFeatures();
  const {showInactiveComponent, showInactive} = useShowInactive();

  const enrichedFeatures = useMemo(
    () =>
      product.features
        ?.map((feature) => ({
          ...feature,
          activeStatus: activeStatus({
            startDate: feature.startDate || product.startDate,
            endDate: feature.endDate || product.endDate,
          }),
          featurePropertiesAccessNumberId: feature.featureProperties?.accessNumberId,
        }))
        .filter(({activeStatus}) => showInactive || activeStatus === 'ACTIVE') || [],
    [product, showInactive]
  );

  const [sortedFeatures, getThSortableProps] = useThSortable<
    ProductFeature & {activeStatus: ActiveStatus; featurePropertiesAccessNumberId: string | undefined}
  >(enrichedFeatures);

  const onDelete = (featureId: string) => {
    const feature = getFeature(product, featureId);
    showModal({
      title: 'Delete feature',
      content: (
        <div>
          {feature && (
            <List>
              <ListItem>
                <span>
                  <b>{getProductTypeFeature(product.productType, feature.featureTypeId)?.name}</b>
                  {feature.featureProperties?.accessNumberId && ' - '}
                  <b>{feature.featureProperties?.accessNumberId}</b>
                </span>
              </ListItem>
            </List>
          )}
          <p>Are you sure you want to delete this feature?</p>
        </div>
      ),
      confirmText: 'Delete',
      confirmType: 'negative',
      onConfirm: () => onConfirmDelete(featureId),
    });
  };

  const onConfirmDelete = (featureId: string) => {
    saveCallback({...product, features: product.features?.filter((feature) => feature.id !== featureId)});
  };

  const {THead, Th, Tr, ThSortable, TBody, Td} = FullWidthTable;

  return (
    <>
      {showInactiveComponent}
      {sortedFeatures.length === 0 ? (
        <InformationLineFc className="marginTop">No active features</InformationLineFc>
      ) : (
        <FullWidthTable>
          <THead>
            <Tr>
              <ThSortable {...getThSortableProps('featureTypeId')}>Feature</ThSortable>
              <ThSortable {...getThSortableProps('featurePropertiesAccessNumberId')}>Properties</ThSortable>
              <ThSortable {...getThSortableProps('startDate')}>Start date</ThSortable>
              <ThSortable {...getThSortableProps('endDate')}>End date</ThSortable>
              <ThSortable {...getThSortableProps('activeStatus')} className="maxWidth10rem">
                Active Status
              </ThSortable>
              {showActions && (
                <>
                  <Th className="minWidth"></Th>
                  <Th className="minWidth"></Th>
                </>
              )}
            </Tr>
          </THead>
          <TBody>
            {sortedFeatures.map((feature, i) => {
              const {id, featureTypeId, startDate, endDate, featureProperties, activeStatus} = feature;
              const productFeatureType = getProductTypeFeature(product.productType, featureTypeId);

              return (
                <Tr key={id} className={classnames({inactive: activeStatus !== 'ACTIVE'})}>
                  <Td>
                    {productFeatureType?.name || featureTypeId}
                    {productFeatureType?.description && <Tooltip>{productFeatureType.description}</Tooltip>}
                  </Td>
                  <Td>
                    {featureProperties?.accessNumberId ? (
                      <span>
                        <i className="disabled">{getAccessNumberName(featureTypeId)} - </i>
                        {featureProperties.accessNumberId}
                      </span>
                    ) : (
                      'no properties'
                    )}
                  </Td>

                  <Td className={classnames({disabled: !startDate})}>
                    {startDate ? (
                      format.date(startDate)
                    ) : (
                      <>
                        {format.date(product.startDate)}
                        <Tooltip>Inherited from service</Tooltip>
                      </>
                    )}
                  </Td>
                  <Td className={classnames({disabled: !endDate})}>
                    {endDate ? (
                      format.date(endDate)
                    ) : product.endDate ? (
                      <>
                        {format.date(product.endDate)}
                        <Tooltip>Inherited from service</Tooltip>
                      </>
                    ) : (
                      'no end'
                    )}
                  </Td>
                  <Td className={classnames(activeStatus.toLowerCase() + 'Period', 'maxWidth10rem')}>{activeStatus}</Td>
                  {showActions && (
                    <>
                      <Td className="minWidth">
                        <Icon
                          icon="edit"
                          className="blueText pointer rem1_25 marginLeft"
                          onClick={() => onUpdate(feature)}
                        />
                      </Td>
                      <Td className="minWidth">
                        <Icon icon="delete" className="redText pointer rem1_25 " onClick={() => onDelete(id)} />
                      </Td>
                    </>
                  )}
                </Tr>
              );
            })}
          </TBody>
        </FullWidthTable>
      )}
    </>
  );
};
